import * as Icons from "lucide-react";
import { Check, ArrowRight, BadgeCheck } from "lucide-react";
import { IMG, ASSETS, HEAT_PUMP_BENEFITS, HEAT_PUMP_SYSTEMS, HEAT_PUMP_FAQS, INSTALL_STEPS } from "@/data/site";
import { PageHero } from "@/components/PageHero";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { QuoteForm } from "@/components/QuoteForm";
import { CtaBanner } from "@/components/CtaBanner";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export default function HeatPumpHotWater() {
  return (
    <>
      <PageHero
        label="Heat Pump Hot Water"
        title="Upgrade to Energy-Efficient Heat Pump Hot Water"
        subtitle="Heat pump hot-water systems use energy from the surrounding air to heat your water far more efficiently than conventional electric resistance systems — for lower running-cost potential and reduced emissions."
        image={IMG.hpInstalled}
        crumbs={[{ label: "Services", to: "/services" }, { label: "Heat Pump Hot Water" }]}
      />

      {/* What is it */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <Reveal>
            <SectionLabel>The Basics</SectionLabel>
            <h2 className="mt-5 font-display text-3xl md:text-4xl font-bold tracking-tight text-brand-blue">
              What is a heat pump hot water system?
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Rather than generating heat directly like a traditional electric element, a heat pump draws warmth from the surrounding air and transfers it into your water tank. Because it moves heat instead of creating it, it uses only a fraction of the electricity — while still delivering plenty of reliable hot water.
            </p>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              It's a modern, proven technology that's quickly becoming the go-to choice for homeowners looking to cut energy use and lower their bills.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="overflow-hidden rounded-3xl border border-slate-200">
              <img src={IMG.hpInstalled} alt="Installed heat pump hot water system" loading="lazy" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Replace existing */}
      <section className="bg-[#FAFAFA] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionLabel>Replace &amp; Upgrade</SectionLabel>
            <h2 className="mt-5 max-w-2xl font-display text-3xl md:text-4xl font-bold tracking-tight text-brand-blue">
              Replace your existing hot water system
            </h2>
            <p className="mt-5 max-w-2xl text-lg text-slate-600 leading-relaxed">
              A heat pump is a smart replacement for older, less efficient systems. We commonly upgrade:
            </p>
          </Reveal>
          <div className="mt-10 grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
            <Reveal>
              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
                <img src={IMG.hpBeforeAfter} alt="Gas hot water system replaced with an energy-efficient heat pump" loading="lazy" className="w-full object-cover" />
              </div>
            </Reveal>
            <div className="grid gap-5">
              {[
                { t: "Gas hot-water systems", d: "Move away from gas and its ongoing supply charges to an efficient electric heat pump." },
                { t: "Old electric storage systems", d: "Replace power-hungry element systems with technology that uses far less electricity." },
                { t: "Inefficient older systems", d: "Upgrade ageing units nearing end-of-life for reliable, modern performance." },
              ].map((c, i) => (
                <Reveal key={c.t} delay={i * 0.08}>
                  <div className="rounded-2xl border border-slate-200 bg-white p-7">
                    <h3 className="font-display text-lg font-semibold text-brand-blue">{c.t}</h3>
                    <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{c.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionLabel>Benefits</SectionLabel>
            <h2 className="mt-5 max-w-2xl font-display text-3xl md:text-4xl font-bold tracking-tight text-brand-blue">
              The benefits of heat pump hot water
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {HEAT_PUMP_BENEFITS.map((b, i) => {
              const Icon = Icons[b.icon] || Icons.Sparkles;
              return (
                <Reveal key={b.title} delay={(i % 3) * 0.07}>
                  <div className="h-full rounded-2xl border border-slate-200 bg-[#FAFAFA] p-7">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-electric/10">
                      <Icon className="h-6 w-6 text-brand-electric" strokeWidth={1.5} />
                    </span>
                    <h3 className="mt-5 font-display text-lg font-semibold text-brand-blue">{b.title}</h3>
                    <p className="mt-2 text-[15px] text-slate-600 leading-relaxed">{b.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* VEU incentive */}
      <section className="bg-brand-blue py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-brand-gold p-8 md:p-14">
              <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-center">
                <div>
                  <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-brand-blue/70">
                    <BadgeCheck className="h-5 w-5" /> Victorian Energy Upgrades
                  </div>
                  <h2 className="mt-5 max-w-3xl font-display text-3xl md:text-4xl font-bold tracking-tight text-brand-blue text-balance">
                    You may be eligible for government-supported incentives
                  </h2>
                  <p className="mt-5 max-w-2xl text-lg text-brand-blue/80 leading-relaxed">
                    Eligible Victorian customers may be able to access government-supported incentives depending on current program rules, property eligibility, and the system being replaced. Our team can talk you through what may apply to your situation and handle the process for you.
                  </p>
                  <p className="mt-4 text-sm text-brand-blue/60">
                    Incentive amounts vary and are not guaranteed. Eligibility is subject to current program rules.
                  </p>
                </div>
                <img src={ASSETS.veuBadge} alt="Victorian Energy Upgrades program badge" loading="lazy" className="mx-auto h-32 w-32 md:h-40 md:w-40 object-contain" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* System options */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionLabel>System Options</SectionLabel>
            <h2 className="mt-5 max-w-2xl font-display text-3xl md:text-4xl font-bold tracking-tight text-brand-blue">
              Trusted heat pump brands we install
            </h2>
            <p className="mt-5 max-w-2xl text-lg text-slate-600 leading-relaxed">
              We'll recommend the right system for your household size and needs. Product images are representative.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {HEAT_PUMP_SYSTEMS.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.08}>
                <div className="group h-full overflow-hidden rounded-2xl border border-slate-200 bg-[#FAFAFA]">
                  <div className="overflow-hidden bg-white">
                    <img src={s.image} alt={`${s.name} heat pump hot water system`} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-7">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-xl font-semibold text-brand-blue">{s.name}</h3>
                      <span className="rounded-full bg-brand-electric/10 px-3 py-1 text-xs font-semibold text-brand-electric">{s.capacity}</span>
                    </div>
                    <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{s.note}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#FAFAFA] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionLabel>Installation Process</SectionLabel>
            <h2 className="mt-5 max-w-2xl font-display text-3xl md:text-4xl font-bold tracking-tight text-brand-blue">
              How your installation works
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-4">
            {INSTALL_STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-7">
                  <div className="font-display text-4xl font-bold text-brand-electric/20">{s.n}</div>
                  <h3 className="mt-4 font-display text-lg font-semibold text-brand-blue">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16 lg:px-8">
          <Reveal>
            <SectionLabel>FAQ</SectionLabel>
            <h2 className="mt-5 font-display text-3xl md:text-4xl font-bold tracking-tight text-brand-blue">
              Heat pump hot water questions, answered
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Still have questions? Our friendly team is happy to help — just get in touch.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="w-full" data-testid="heatpump-faq">
              {HEAT_PUMP_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
                  <AccordionTrigger className="text-left font-display text-lg font-medium text-brand-blue hover:no-underline [&>svg]:text-brand-electric">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] text-slate-600 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Quote */}
      <section className="bg-[#FAFAFA] py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <Reveal>
            <SectionLabel>Request a Free Quote</SectionLabel>
            <h2 className="mt-5 font-display text-3xl md:text-4xl font-bold tracking-tight text-brand-blue">
              Ready to upgrade your hot water?
            </h2>
            <p className="mt-5 max-w-md text-lg text-slate-600 leading-relaxed">
              Request a free quote and we'll assess your property, recommend a suitable system, and handle a professional installation from start to finish.
            </p>
            <ul className="mt-8 space-y-3">
              {["Free site assessment", "Rebate eligibility guidance", "Professional installation", "Testing & handover"].map((x) => (
                <li key={x} className="flex items-center gap-3 text-brand-blue font-medium">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-electric/10"><Check className="h-3.5 w-3.5 text-brand-electric" /></span>
                  {x}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8">
              <QuoteForm defaultService="Heat Pump Hot Water" />
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
